import React, { useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, X, User, Wallet, GitBranch } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatApiError } from '../utils/apiErrors';
import { fetchPaginatedCollection } from '../utils/apiPagination';
import { formatUiDate as formatDate, formatUiCurrencyCompact } from '../utils/uiFormat';
import UiDateField from '../components/ui/UiDateField';
import SalaryDueDaySelect from '../components/SalaryDueDaySelect';
import SortableHeader from '../components/SortableHeader';
import useSortable from '../hooks/useSortable';
import { salaryDueDayLabel } from '../utils/salaryDueDay';

const getLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const FINANCIAL_EVENT_TYPES = ['salary', 'advance', 'bonus', 'gift', 'deduction'];
const EVENT_TYPE_OPTIONS = {
  financial: [
    { value: 'salary', label: 'Salary Paid' },
    { value: 'advance', label: 'Advance Taken' },
    { value: 'bonus', label: 'Bonus Paid' },
    { value: 'gift', label: '🎁 Gift Given' },
    { value: 'deduction', label: 'Deduction Made' },
  ],
  lifecycle: [
    { value: 'employed', label: 'Employed' },
    { value: 'reemployed', label: 'Re-employed' },
    { value: 'terminated', label: 'Terminated' },
    { value: 'status_change', label: 'Status Change' },
    { value: 'department_change', label: 'Department Change' },
    { value: 'designation_change', label: 'Designation Changed' },
    { value: 'salary_change', label: 'Salary Change' },
    { value: 'salary_date_change', label: 'Salary Due Day Change' },
  ],
};
const sourceFromType = (t) => (FINANCIAL_EVENT_TYPES.includes(t) ? 'financial' : 'lifecycle');
const eventTypeLabel = (t) =>
  [...EVENT_TYPE_OPTIONS.financial, ...EVENT_TYPE_OPTIONS.lifecycle].find((o) => o.value === t)?.label || t;

const initialBio = {
  first_name: '', last_name: '', phone: '', email: '', address: '', department: '',
  designation: '', hire_date: getLocalDateString(), employment_status: 'active',
  monthly_salary: '', salary_date: '1', wage_type: 'daily_wages', wage_type_effective_date: '',
  notes: '', emergency_contact_name: '', emergency_contact_phone: '', user: '',
};
const initialEvent = {
  source: 'financial', id: null, event_type: 'salary', event_date: getLocalDateString(),
  amount: '', status: 'approved', reason: '', department: '', monthly_salary: '',
  salary_date: '', designation: '', employment_status_value: 'active',
};

const normalizeEvents = (financial, lifecycle, departments) => {
  const fin = (financial || []).map((i) => ({
    key: `financial-${i.id}`, source: 'financial', id: i.id, staff: i.staff,
    event_type: i.payment_type, event_type_label: i.payment_type_label || eventTypeLabel(i.payment_type),
    event_date: i.advance_date, amount: parseFloat(i.amount || 0), reason: i.reason || '', status: i.status || '',
    summary: '',
  }));
  const life = (lifecycle || []).map((i) => ({
    key: `lifecycle-${i.id}`, source: 'lifecycle', id: i.id, staff: i.staff,
    event_type: i.event_type, event_type_label: i.event_type_label || eventTypeLabel(i.event_type),
    event_date: i.event_date,
    // Salary changes show their new pay as a bold amount, like a salary payment.
    amount: i.event_type === 'salary_change' && i.monthly_salary != null ? parseFloat(i.monthly_salary) : null,
    reason: i.reason || '',
    department: i.department || '', monthly_salary: i.monthly_salary == null ? '' : String(i.monthly_salary),
    salary_date: i.salary_date == null ? '' : String(i.salary_date),
    designation: i.designation || '',
    employment_status_value: i.employment_status_value || '',
    summary:
      i.event_type === 'department_change' ? (i.department_name || (departments.find((d) => String(d.id) === String(i.department))?.name) || 'No Department')
      : i.event_type === 'designation_change' ? (i.designation || '—')
      : i.event_type === 'salary_change' ? ''
      : i.event_type === 'salary_date_change' ? salaryDueDayLabel(i.salary_date)
      : i.event_type === 'status_change' ? (i.employment_status_value_label || i.employment_status_value || '')
      : '',  // reason is shown separately below — don't duplicate it as the summary
  }));
  return [...fin, ...life].sort((a, b) => String(b.event_date || '').localeCompare(String(a.event_date || '')) || String(b.id || '').localeCompare(String(a.id || '')));
};

const badgeClass = (ev) =>
  ev.source === 'lifecycle' ? 'bg-slate-100 text-slate-700'
  : ev.event_type === 'deduction' ? 'bg-red-100 text-red-700'
  : 'bg-green-100 text-green-700';

const getWageLabel = (w) => (w === 'daily_wages' ? 'Daily Wages' : 'Monthly Salaried');

/**
 * StaffTab — self-contained staff list + consolidated per-staff editor.
 * The Edit button opens ONE modal with 3 tabs (Bio | Financial | Lifecycle):
 * each tab has a form on top and a shared chronological event list at the bottom.
 */
export default function StaffTab({ staff = [], departments = [], users = [], hasPermission, onDataChanged }) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);      // staff being edited; {} = new
  const [tab, setTab] = useState('bio');             // bio | pay_type | salary_due
  const [showEventModal, setShowEventModal] = useState(false);
  const [bioForm, setBioForm] = useState(initialBio);
  const [financialForm, setFinancialForm] = useState({ wage_type: 'daily_wages', rate: '', effective_date: '', editingEventId: null, salary_date: '', salary_date_effective: '' });
  const [eventForm, setEventForm] = useState(initialEvent);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [savingBio, setSavingBio] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);

  const canEdit = !hasPermission || hasPermission('staff', 'edit') || hasPermission('staff', 'add');
  const canDelete = !hasPermission || hasPermission('staff', 'delete');

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? staff.filter((s) => `${s.first_name || ''} ${s.last_name || ''} ${s.designation || ''}`.toLowerCase().includes(q))
      : staff;
    return [...rows].sort((a, b) => {
      const at = a.employment_status === 'terminated' ? 1 : 0;
      const bt = b.employment_status === 'terminated' ? 1 : 0;
      if (at !== bt) return at - bt;
      return (a.first_name || '').localeCompare(b.first_name || '');
    });
  }, [staff, search]);

  // Column sorting — falls back to the default (terminated-last, then name) when no column picked.
  const { sortKey, sortDir, handleSort, sortData } = useSortable('', 'asc');
  const displayStaff = sortData(filteredStaff);

  const loadEvents = async (staffId) => {
    if (!staffId) { setEvents([]); return; }
    setEventsLoading(true);
    try {
      const [fin, life] = await Promise.all([
        fetchPaginatedCollection(api, '/staff-advances/', { pageSize: 500, maxRecords: 50000, params: { staff_id: staffId } }),
        fetchPaginatedCollection(api, '/staff-lifecycle-events/', { pageSize: 500, maxRecords: 10000, params: { staff_id: staffId } }),
      ]);
      setEvents(normalizeEvents(fin, life, departments));
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load staff events'));
    } finally {
      setEventsLoading(false);
    }
  };

  const openCreate = () => {
    setEditing({});
    setTab('bio');
    setBioForm({ ...initialBio, hire_date: getLocalDateString() });
    setEvents([]);
    setEventForm({ ...initialEvent, event_type: 'salary', event_date: getLocalDateString() });
  };

  const openEdit = (member) => {
    setEditing(member);
    setTab('bio');
    // The Bio "Hire Date" is the FIRST employment date (earliest 'employed' event);
    // re-employments are added via Add Event, not by editing this field.
    setBioForm({ ...initialBio, ...member, hire_date: member.first_employment_date || member.hire_date, department: member.department || '', user: member.user || '' });
    setFinancialForm({ wage_type: member.wage_type || 'daily_wages', rate: member.monthly_salary == null ? '' : String(member.monthly_salary), effective_date: '', editingEventId: null, salary_date: member.salary_date == null ? '' : String(member.salary_date), salary_date_effective: '' });
    setEventForm({ ...initialEvent, event_date: getLocalDateString() });
    loadEvents(member.id);
  };

  const close = () => { setEditing(null); setEvents([]); };

  const refresh = () => { onDataChanged && onDataChanged(); if (editing?.id) loadEvents(editing.id); };

  // ── Save Bio (create or update staff). New staff w/ salary → also creates a salary event ──
  const saveBio = async (e) => {
    e.preventDefault();
    if (!bioForm.first_name) { toast.error('Enter first name'); return; }
    setSavingBio(true);
    try {
      const payload = {
        ...bioForm,
        monthly_salary: bioForm.monthly_salary === '' ? 0 : Number(bioForm.monthly_salary),
        salary_date: bioForm.salary_date === '' ? 1 : Number(bioForm.salary_date),
        department: bioForm.department || null,
        user: bioForm.user || null,
      };
      if (editing?.id) {
        await api.patch(`/staff/${editing.id}/`, payload);
        toast.success('Staff updated');
        onDataChanged && onDataChanged();
      } else {
        const res = await api.post('/staff/', payload);
        const newStaff = res.data;
        toast.success('Staff added');
        // The backend anchors the 'employed' + initial salary events on hire; no need to add here.
        onDataChanged && onDataChanged();
        setEditing(newStaff);
        loadEvents(newStaff.id);
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save staff'));
    } finally {
      setSavingBio(false);
    }
  };

  // ── Apply pay-type and/or salary-rate change (with effective date) ──
  const applyFinancial = async () => {
    if (!editing?.id) { toast.error('Save the staff bio first'); return; }
    const effDate = financialForm.effective_date || getLocalDateString();
    const editId = financialForm.editingEventId;
    // Current pay rate = latest salary_change event, else the staff record's monthly_salary.
    const latestSalaryEvent = events.filter((e) => e.event_type === 'salary_change')
      .sort((a, b) => String(b.event_date || '').localeCompare(String(a.event_date || '')))[0];
    const currentRate = latestSalaryEvent ? Number(latestSalaryEvent.monthly_salary || 0) : Number(editing.monthly_salary || 0);
    const rateProvided = financialForm.rate !== '';
    const rateChanged = rateProvided && Number(financialForm.rate) !== currentRate;
    const wageChanged = financialForm.wage_type !== editing.wage_type;
    const explicitDate = !!financialForm.effective_date;
    // Allow re-recording the pay type on a chosen (possibly different) date even when the
    // value matches the current staff record — record it whenever the type changed, or when
    // the user set an effective date and isn't just changing the rate.
    const recordWage = wageChanged || (explicitDate && !rateChanged && !editId);

    const jobs = [];
    if (recordWage) {
      jobs.push(api.patch(`/staff/${editing.id}/`, { wage_type: financialForm.wage_type, wage_type_effective_date: effDate }));
    }
    if (editId) {
      // Editing an existing salary change — update it (date/amount) even if the value is unchanged.
      if (!rateProvided) { toast.error('Enter a salary/rate'); return; }
      jobs.push(api.patch(`/staff-lifecycle-events/${editId}/`, {
        staff: editing.id, event_type: 'salary_change', event_date: effDate,
        monthly_salary: Number(financialForm.rate), reason: 'Pay rate change',
      }));
    } else if (rateChanged) {
      jobs.push(api.post('/staff-lifecycle-events/', {
        staff: editing.id, event_type: 'salary_change', event_date: effDate,
        monthly_salary: Number(financialForm.rate), reason: 'Pay rate change',
      }));
    }
    if (jobs.length === 0) { toast.error('No change to apply — change the pay type or rate, or pick an effective date'); return; }
    setSavingEvent(true);
    try {
      await Promise.all(jobs);
      toast.success('Pay updated');
      onDataChanged && onDataChanged();
      setEditing((prev) => ({ ...prev, wage_type: financialForm.wage_type, monthly_salary: financialForm.rate }));
      setFinancialForm((f) => ({ ...f, editingEventId: null }));
      loadEvents(editing.id);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update pay'));
    } finally {
      setSavingEvent(false);
    }
  };

  // Salary due day — recorded as a salary_date_change lifecycle event with its own
  // effective date (past dates allowed) so the Salary tabs resolve it historically.
  const applySalaryDay = async () => {
    if (!editing?.id) { toast.error('Save the staff bio first'); return; }
    const day = Number(financialForm.salary_date);
    if (financialForm.salary_date === '' || !(day >= 1 && day <= 31)) { toast.error('Enter a salary due day (1–31)'); return; }
    const effDate = financialForm.salary_date_effective || getLocalDateString();
    setSavingEvent(true);
    try {
      await api.post('/staff-lifecycle-events/', {
        staff: editing.id, event_type: 'salary_date_change', event_date: effDate,
        salary_date: day, reason: 'Salary due day change',
      });
      toast.success('Salary due day updated');
      onDataChanged && onDataChanged();
      loadEvents(editing.id);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update salary due day'));
    } finally {
      setSavingEvent(false);
    }
  };

  const deleteStaff = async (member) => {
    if (!window.confirm(`Delete ${member.first_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/staff/${member.id}/`);
      toast.success('Staff deleted');
      onDataChanged && onDataChanged();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to delete staff'));
    }
  };

  // ── Save an event (financial or lifecycle) ──
  const saveEvent = async () => {
    if (!editing?.id) { toast.error('Save the staff bio first'); return; }
    const t = eventForm.event_type;
    const isLifecycle = sourceFromType(t) === 'lifecycle';
    if (!eventForm.event_date) { toast.error('Select a date'); return; }
    if (!isLifecycle && eventForm.amount === '') { toast.error('Enter amount'); return; }
    if (t === 'salary_change' && eventForm.monthly_salary === '') { toast.error('Enter salary'); return; }
    if (t === 'salary_date_change' && eventForm.salary_date === '') { toast.error('Enter salary due day'); return; }
    if (t === 'designation_change' && !eventForm.designation) { toast.error('Enter the new designation'); return; }
    setSavingEvent(true);
    try {
      const endpoint = isLifecycle ? 'staff-lifecycle-events' : 'staff-advances';
      const payload = isLifecycle
        ? {
            staff: editing.id, event_type: t, event_date: eventForm.event_date,
            department: t === 'department_change' ? (eventForm.department || null) : null,
            monthly_salary: t === 'salary_change' ? (eventForm.monthly_salary === '' ? 0 : Number(eventForm.monthly_salary)) : null,
            salary_date: t === 'salary_date_change' ? (eventForm.salary_date === '' ? null : Number(eventForm.salary_date)) : null,
            designation: t === 'designation_change' ? eventForm.designation : null,
            employment_status_value: t === 'status_change' ? eventForm.employment_status_value : null,
            reason: eventForm.reason || '',
          }
        : {
            staff: editing.id, payment_type: t, advance_date: eventForm.event_date,
            amount: eventForm.amount === '' ? 0 : Number(eventForm.amount),
            status: eventForm.status || 'approved', repaid_amount: 0, reason: eventForm.reason || '',
          };
      if (eventForm.id) await api.patch(`/${endpoint}/${eventForm.id}/`, payload);
      else await api.post(`/${endpoint}/`, payload);
      toast.success(eventForm.id ? 'Event updated' : 'Event added');
      setEventForm({ ...initialEvent, event_type: t, event_date: getLocalDateString() });
      setShowEventModal(false);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save event'));
    } finally {
      setSavingEvent(false);
    }
  };

  const editEvent = (ev) => {
    if (ev.source === 'financial') {
      // Deductions are made on the Performance tab; the rest on the Expenses page.
      if (ev.event_type === 'deduction') {
        toast('Deductions are managed on the Staff → Performance tab.', { icon: 'ℹ️' });
      } else {
        toast('Advances, salary payments, bonuses and gifts are managed on the Expenses page.', { icon: 'ℹ️' });
      }
      return;
    }
    // Salary changes are edited on the Pay Type tab (they set the pay rate).
    if (ev.event_type === 'salary_change') {
      setTab('pay_type');
      setFinancialForm({ wage_type: editing.wage_type || 'daily_wages', rate: ev.monthly_salary || '', effective_date: ev.event_date || '', editingEventId: ev.id });
      return;
    }
    // All other events edit in the Add/Edit Event popup.
    setEventForm({
      ...initialEvent, source: 'lifecycle', id: ev.id, event_type: ev.event_type,
      event_date: ev.event_date, amount: '',
      reason: ev.reason || '', department: ev.department || '',
      monthly_salary: ev.monthly_salary || '', salary_date: ev.salary_date || '',
      designation: ev.designation || '',
      employment_status_value: ev.employment_status_value || 'active',
    });
    setShowEventModal(true);
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm('Delete this event?')) return;
    const endpoint = ev.source === 'financial' ? 'staff-advances' : 'staff-lifecycle-events';
    try {
      await api.delete(`/${endpoint}/${ev.id}/`);
      toast.success('Event deleted');
      refresh();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to delete event'));
    }
  };

  // Already-employed staff can only be re-employed (their bio already exists).
  const isEmployed = !!(editing?.id) && (editing?.employment_status !== 'terminated');
  const openAddEvent = () => {
    setEventForm({ ...initialEvent, source: 'lifecycle', event_type: isEmployed ? 'reemployed' : 'status_change', event_date: getLocalDateString() });
    setShowEventModal(true);
  };
  const closeEventModal = () => {
    setShowEventModal(false);
    setEventForm({ ...initialEvent, source: 'lifecycle', event_type: 'status_change', event_date: getLocalDateString() });
  };

  const inputCls = 'w-full px-3 py-2 border rounded-lg';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff…"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm w-full sm:w-64" />
        {canEdit && (
          <button onClick={openCreate} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            Add Staff
          </button>
        )}
      </div>

      {/* Staff list */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader label="Name" col="first_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Employed" col="first_employment_date" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Pay Type" col="wage_type" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Status" col="employment_status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {displayStaff.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No staff found</td></tr>
            ) : displayStaff.map((m) => (
              <tr key={m.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">
                  {m.full_name || `${m.first_name || ''} ${m.last_name || ''}`.trim()}
                  {m.employment_status === 'terminated' && <span className="text-red-500 text-xs"> (Terminated)</span>}
                </td>
                <td className="px-4 py-3 text-gray-600">{(m.first_employment_date || m.hire_date) ? formatDate(m.first_employment_date || m.hire_date) : '—'}</td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold ${m.wage_type === 'daily_wages' ? 'text-amber-700' : 'text-blue-700'}`}>{getWageLabel(m.wage_type)}</span></td>
                <td className="px-4 py-3 capitalize text-gray-600">{m.employment_status}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    {canEdit && <button onClick={() => openEdit(m)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><Edit2 size={16} /></button>}
                    {canDelete && <button onClick={() => deleteStaff(m)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 size={16} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Consolidated editor */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-lg font-bold text-gray-800">
                {editing.id ? (editing.full_name || `${editing.first_name || ''} ${editing.last_name || ''}`.trim()) : 'New Staff'}
              </h2>
              <button onClick={close} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>

            {/* Tab bar */}
            <div className="flex border-b bg-gray-50">
              {[
                { id: 'bio', label: 'Bio', icon: User },
                { id: 'pay_type', label: 'Pay Type', icon: Wallet },
                { id: 'salary_due', label: 'Salary Due Day', icon: GitBranch },
              ].map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  disabled={t.id !== 'bio' && !editing.id}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition ${tab === t.id ? 'bg-white text-primary-700 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'} ${t.id !== 'bio' && !editing.id ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  <t.icon size={15} /> {t.label}
                </button>
              ))}
            </div>

            {/* Body: form (top) + event list (bottom) */}
            <div className="flex-1 overflow-y-auto">
              {/* ── FORM AREA ── */}
              <div className="p-4 border-b">
                {tab === 'bio' && (
                  <form onSubmit={saveBio} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className={labelCls}>First Name *</label><input value={bioForm.first_name} onChange={(e) => setBioForm({ ...bioForm, first_name: e.target.value })} className={inputCls} required /></div>
                    <div><label className={labelCls}>Last Name</label><input value={bioForm.last_name} onChange={(e) => setBioForm({ ...bioForm, last_name: e.target.value })} className={inputCls} /></div>
                    <div><label className={labelCls}>Phone</label><input value={bioForm.phone} onChange={(e) => setBioForm({ ...bioForm, phone: e.target.value })} className={inputCls} /></div>
                    <div><label className={labelCls}>Email</label><input value={bioForm.email} onChange={(e) => setBioForm({ ...bioForm, email: e.target.value })} className={inputCls} /></div>
                    <div><label className={labelCls}>First Employment Date *</label><UiDateField value={bioForm.hire_date} onChange={(v) => setBioForm({ ...bioForm, hire_date: v })} /><p className="text-[11px] text-gray-400 mt-1">Original join date. Re-employments are added via Add Event.</p></div>
                    {!editing.id && (
                      <>
                        <div><label className={labelCls}>Department</label>
                          <select value={bioForm.department} onChange={(e) => setBioForm({ ...bioForm, department: e.target.value })} className={inputCls}>
                            <option value="">No Department</option>
                            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        </div>
                        <div><label className={labelCls}>Status</label>
                          <select value={bioForm.employment_status} onChange={(e) => setBioForm({ ...bioForm, employment_status: e.target.value })} className={inputCls}>
                            <option value="active">Active</option><option value="inactive">Inactive</option><option value="on_leave">On Leave</option><option value="terminated">Terminated</option>
                          </select>
                        </div>
                        <div><label className={labelCls}>Monthly Salary / Wage</label><input type="number" value={bioForm.monthly_salary} onChange={(e) => setBioForm({ ...bioForm, monthly_salary: e.target.value })} className={inputCls} min="0" step="0.01" placeholder="0.00" /></div>
                        <div><label className={labelCls}>Pay Type</label>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setBioForm({ ...bioForm, wage_type: 'salaried' })} className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium ${bioForm.wage_type === 'salaried' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700'}`}>Salaried</button>
                            <button type="button" onClick={() => setBioForm({ ...bioForm, wage_type: 'daily_wages' })} className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium ${bioForm.wage_type === 'daily_wages' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700'}`}>Daily Wages</button>
                          </div>
                        </div>
                      </>
                    )}
                    {!editing.id && <div><label className={labelCls}>Salary Due Day</label><SalaryDueDaySelect value={bioForm.salary_date} onChange={(value) => setBioForm({ ...bioForm, salary_date: value })} className={inputCls} /></div>}
                    <div><label className={labelCls}>Emergency Contact Name</label><input value={bioForm.emergency_contact_name} onChange={(e) => setBioForm({ ...bioForm, emergency_contact_name: e.target.value })} className={inputCls} /></div>
                    <div><label className={labelCls}>Emergency Contact Phone</label><input value={bioForm.emergency_contact_phone} onChange={(e) => setBioForm({ ...bioForm, emergency_contact_phone: e.target.value })} className={inputCls} /></div>
                    <div><label className={labelCls}>Linked User Account</label>
                      <select value={bioForm.user || ''} onChange={(e) => setBioForm({ ...bioForm, user: e.target.value })} className={inputCls}>
                        <option value="">— None —</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.username} ({u.role_name || u.role})</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-2"><label className={labelCls}>Address</label><textarea value={bioForm.address} onChange={(e) => setBioForm({ ...bioForm, address: e.target.value })} className={inputCls} rows="2" /></div>
                    <div className="md:col-span-2"><label className={labelCls}>Notes</label><textarea value={bioForm.notes} onChange={(e) => setBioForm({ ...bioForm, notes: e.target.value })} className={inputCls} rows="2" /></div>
                    <div className="md:col-span-2 flex justify-end">
                      <button type="submit" disabled={savingBio} className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{savingBio ? 'Saving…' : (editing.id ? 'Save Bio' : 'Create Staff')}</button>
                    </div>
                  </form>
                )}

                {tab === 'pay_type' && (
                  <div className="rounded-lg border p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Pay Type &amp; Rate</p>
                    <p className="text-[11px] text-gray-400 mb-3">Change the pay type and/or the salary/wage rate — either one or both together — effective from the date below (past dates allowed).</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Pay Type</label>
                        <select value={financialForm.wage_type} onChange={(e) => setFinancialForm({ ...financialForm, wage_type: e.target.value })} className={inputCls}>
                          <option value="salaried">Monthly Salaried</option>
                          <option value="daily_wages">Daily Wages</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>{financialForm.wage_type === 'daily_wages' ? 'Monthly rate (daily = rate ÷ 30)' : 'Monthly Salary'}</label>
                        <input type="number" value={financialForm.rate} onChange={(e) => setFinancialForm({ ...financialForm, rate: e.target.value })} className={inputCls} min="0" step="0.01" placeholder="0.00" />
                      </div>
                      <div>
                        <label className={labelCls}>Effective Date</label>
                        <UiDateField value={financialForm.effective_date} onChange={(v) => setFinancialForm({ ...financialForm, effective_date: v })} placeholder="Defaults to today" />
                      </div>
                    </div>
                    <div className="flex justify-end mt-3">
                      <button type="button" onClick={applyFinancial} disabled={savingEvent} className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{savingEvent ? 'Saving…' : 'Save Change'}</button>
                    </div>
                  </div>
                )}

                {tab === 'salary_due' && (
                  <div className="rounded-lg border p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Salary Due Day</p>
                    <p className="text-[11px] text-gray-400 mb-3">The day of the month salary falls due. Effective from the date below (past dates allowed) — this feeds the Salary tabs on the Staff and Expense pages.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Due Day</label>
                        <SalaryDueDaySelect value={financialForm.salary_date} onChange={(value) => setFinancialForm({ ...financialForm, salary_date: value })} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Effective Date</label>
                        <UiDateField value={financialForm.salary_date_effective} onChange={(v) => setFinancialForm({ ...financialForm, salary_date_effective: v })} placeholder="Defaults to today" />
                      </div>
                    </div>
                    <div className="flex justify-end mt-3">
                      <button type="button" onClick={applySalaryDay} disabled={savingEvent} className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{savingEvent ? 'Saving…' : 'Save Due Day'}</button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── EVENT LIST (shared) ── */}
              {editing.id && (
                <div>
                  <div className="px-4 py-2 flex items-center justify-between bg-gray-50 border-b sticky top-0">
                    <span className="text-xs font-semibold text-gray-500 uppercase">All Events ({events.length})</span>
                    <button onClick={openAddEvent} className="px-2.5 py-1 text-xs text-white bg-primary-600 hover:bg-primary-700 rounded flex items-center gap-1">Add Event</button>
                  </div>
                  {eventsLoading ? (
                    <div className="p-6 text-sm text-gray-400">Loading events…</div>
                  ) : events.length === 0 ? (
                    <div className="p-6 text-sm text-gray-400">No events yet.</div>
                  ) : (
                    <div className="divide-y">
                      {events.map((ev) => (
                        <div key={ev.key} className={`px-4 py-2.5 flex items-start justify-between gap-3 hover:bg-gray-50 ${eventForm.id === ev.id ? 'bg-primary-50/50' : ''}`}>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badgeClass(ev)}`}>{ev.event_type_label}</span>
                              <span className="text-sm text-gray-700">{formatDate(ev.event_date)}</span>
                              {ev.amount != null && <span className="text-sm font-semibold text-gray-800">{formatUiCurrencyCompact(ev.amount)}</span>}
                              {ev.summary && <span className="text-xs text-gray-500">{ev.summary}</span>}
                            </div>
                            {ev.reason && <p className="text-xs text-gray-400 mt-0.5">{ev.reason}</p>}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => editEvent(ev)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit"><Edit2 size={15} /></button>
                            <button onClick={() => deleteEvent(ev)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 size={15} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Event popup */}
      {editing?.id && showEventModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-base font-bold text-gray-800">{eventForm.id ? 'Edit' : 'Add'} Event</h3>
              <button onClick={closeEventModal} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className={labelCls}>Type</label>
                  <select value={eventForm.event_type} onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })} className={inputCls}>
                    {EVENT_TYPE_OPTIONS.lifecycle
                      .filter((o) => o.value !== 'salary_change')
                      .filter((o) => !(o.value === 'employed' && isEmployed && !eventForm.id))
                      .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {isEmployed && !eventForm.id && <p className="text-[11px] text-gray-400 mt-1">This staff is already employed — use Re-employed for a new spell.</p>}
                </div>
                <div><label className={labelCls}>Date</label><UiDateField value={eventForm.event_date} onChange={(v) => setEventForm({ ...eventForm, event_date: v })} /></div>
                {eventForm.event_type === 'department_change' && (
                  <div><label className={labelCls}>Department</label>
                    <select value={eventForm.department} onChange={(e) => setEventForm({ ...eventForm, department: e.target.value })} className={inputCls}>
                      <option value="">No Department</option>
                      {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                )}
                {eventForm.event_type === 'salary_date_change' && (
                  <div><label className={labelCls}>Salary Due Day</label><SalaryDueDaySelect value={eventForm.salary_date} onChange={(value) => setEventForm({ ...eventForm, salary_date: value })} className={inputCls} /></div>
                )}
                {eventForm.event_type === 'designation_change' && (
                  <div><label className={labelCls}>New Designation</label><input value={eventForm.designation} onChange={(e) => setEventForm({ ...eventForm, designation: e.target.value })} className={inputCls} placeholder="e.g. Cashier, Manager" /></div>
                )}
                {eventForm.event_type === 'status_change' && (
                  <div><label className={labelCls}>New Status</label>
                    <select value={eventForm.employment_status_value} onChange={(e) => setEventForm({ ...eventForm, employment_status_value: e.target.value })} className={inputCls}>
                      <option value="active">Active</option><option value="inactive">Inactive</option><option value="on_leave">On Leave</option><option value="terminated">Terminated</option>
                    </select>
                  </div>
                )}
                <div className="md:col-span-2"><label className={labelCls}>Reason / Notes</label><input value={eventForm.reason} onChange={(e) => setEventForm({ ...eventForm, reason: e.target.value })} className={inputCls} /></div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={closeEventModal} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
                <button type="button" onClick={saveEvent} disabled={savingEvent} className="px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{savingEvent ? 'Saving…' : (eventForm.id ? 'Update Event' : 'Add Event')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
